import { useState, useCallback } from 'react';
import { useClickOutsideRef } from '@picsart/design-system/components/hooks';
import useStyles from './styles';
import { ChevronDownIcon } from '@/components/icons';

export type SizeValue = 'small' | 'medium' | 'large';

interface SizeSelectorProps {
  value: SizeValue;
  onChange: (value: SizeValue) => void;
}

const SIZE_OPTIONS: { value: SizeValue; label: string }[] = [
  { value: 'small', label: 'S' },
  { value: 'medium', label: 'M' },
  { value: 'large', label: 'L' },
];

export function SizeSelector({ value, onChange }: SizeSelectorProps) {
  const classes = useStyles();
  const [isOpen, setIsOpen] = useState(false);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const containerRef = useClickOutsideRef(handleClose);

  const handleSelect = useCallback(
    (size: SizeValue) => {
      onChange(size);
      setIsOpen(false);
    },
    [onChange]
  );

  const currentLabel =
    SIZE_OPTIONS.find((opt) => opt.value === value)?.label || 'M';

  return (
    <div className={classes.container} ref={containerRef}>
      <button
        type="button"
        className={classes.button}
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Grid size"
        aria-expanded={isOpen}
      >
        {currentLabel}
        <ChevronDownIcon
          className={`${classes.icon} ${isOpen ? classes.iconOpen : ''}`}
        />
      </button>
      {isOpen && (
        <div className={classes.menu} role="menu">
          {SIZE_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`${classes.menuItem} ${
                value === option.value ? classes.menuItemActive : ''
              }`}
              onClick={() => handleSelect(option.value)}
              role="menuitem"
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default SizeSelector;
